import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CheckCircleIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';

const AdminWithdrawalNotifications = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to pending withdrawals
    const q = query(
      collection(db, 'withdrawalRequests'),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Sort in memory instead
      setPendingRequests(requests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleApprove = async (request) => {
    try {
      // Update withdrawal request status
      await updateDoc(doc(db, 'withdrawalRequests', request.id), {
        status: 'approved',
        processedAt: new Date().toISOString()
      });

      // Update user's account
      const userRef = doc(db, 'users', request.userId);
      await updateDoc(userRef, {
        'accountDetails.pendingWithdrawals': increment(-request.amount),
        'accountDetails.lastWithdrawal': request.amount,
        'balance': increment(-request.amount)
      });

      // Remove from pending requests list
      setPendingRequests(prev => prev.filter(req => req.id !== request.id));
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      alert('Failed to approve withdrawal. Please try again.');
    }
  };

  const handleReject = async (request) => {
    try {
      // Update withdrawal request status
      await updateDoc(doc(db, 'withdrawalRequests', request.id), {
        status: 'rejected',
        processedAt: new Date().toISOString()
      });

      // Update user's pending withdrawals
      const userRef = doc(db, 'users', request.userId);
      await updateDoc(userRef, {
        'accountDetails.pendingWithdrawals': increment(-request.amount)
      });

      // Remove from pending requests list
      setPendingRequests(prev => prev.filter(req => req.id !== request.id));
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      alert('Failed to reject withdrawal. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 p-4">
        <h2 className="text-xl font-semibold text-gray-900">Pending Withdrawals</h2>
        <p className="mt-1 text-sm text-gray-500">
          {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}
        </p>
      </div>
      
      <div className="divide-y divide-gray-200">
        <AnimatePresence>
          {pendingRequests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              className="p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-sm font-medium text-gray-900">
                      {request.userDetails.fullName}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {request.userDetails.accountLevel}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    <p>Amount: <span className="font-medium text-gray-900">${request.amount.toLocaleString()}</span></p>
                    <p>Method: {request.method}</p>
                    <p className="text-xs">Requested: {new Date(request.timestamp).toLocaleString()}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedRequest(request)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <EyeIcon className="h-4 w-4 mr-1.5" />
                    Details
                  </button>
                  <button
                    onClick={() => handleApprove(request)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(request)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1.5" />
                    Reject
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {pendingRequests.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No pending withdrawal requests
          </div>
        )}
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Withdrawal Request Details</h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">User</p>
                  <p className="mt-1">{selectedRequest.userDetails.fullName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="mt-1">{selectedRequest.userEmail}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Amount</p>
                  <p className="mt-1">${selectedRequest.amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Method</p>
                  <p className="mt-1">{selectedRequest.method}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Account Level</p>
                  <p className="mt-1">{selectedRequest.userDetails.accountLevel}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Country</p>
                  <p className="mt-1">{selectedRequest.userDetails.country}</p>
                </div>
              </div>

              {selectedRequest.method === 'bank' && selectedRequest.bankDetails && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Bank Name</p>
                      <p className="mt-1">{selectedRequest.bankDetails.bankName}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Account Number</p>
                      <p className="mt-1">{selectedRequest.bankDetails.accountNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Routing Number</p>
                      <p className="mt-1">{selectedRequest.bankDetails.routingNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Account Holder</p>
                      <p className="mt-1">{selectedRequest.bankDetails.accountHolder}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-500">Bank Address</p>
                    <p className="mt-1">{selectedRequest.bankDetails.bankAddress}</p>
                  </div>
                </div>
              )}

              {selectedRequest.method !== 'bank' && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Wallet Address</h4>
                  <p className="text-sm break-all">{selectedRequest.address}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500">
                  Requested at: {new Date(selectedRequest.timestamp).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => handleReject(selectedRequest)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedRequest)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Approve
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawalNotifications; 